/**
 * Finance Redux Selectors
 *
 * Selectors for finance state including balance data.
 * Balance values are CALCULATED from transactions on the backend.
 * Related: docs/engineering/CHARGE_SYSTEM.md
 */

import { createSelector } from '@reduxjs/toolkit'
import type { FinanceState } from './finance.slice'

interface RootState { finance: FinanceState }

// Base selectors
export const selectFinanceState = (state: RootState) => state.finance
export const selectTransactions = (state: RootState) => state.finance.transactions
export const selectPayments = (state: RootState) => state.finance.payments
export const selectStatement = (state: RootState) => state.finance.statement
export const selectFinanceStats = (state: RootState) => state.finance.stats
export const selectFinanceFilter = (state: RootState) => state.finance.filter
export const selectFinanceLoading = (state: RootState) => state.finance.loading
export const selectFinanceError = (state: RootState) => state.finance.error

// Balance selectors - data is CALCULATED on backend
export const selectBalance = (state: RootState) => state.finance.balance
export const selectBalanceLoading = (state: RootState) => state.finance.balanceLoading
export const selectBalanceError = (state: RootState) => state.finance.balanceError
export const selectLastBalanceCheck = (state: RootState) => state.finance.lastBalanceCheck

/** Available balance = creditLimit - usedBalance + allowance */
export const selectAvailableBalance = createSelector(
  [selectBalance],
  (balance) => balance?.availableBalance ?? 0
)

/** Credit limit for the customer */
export const selectCreditLimit = createSelector(
  [selectBalance],
  (balance) => balance?.creditLimit ?? 0
)

/** Used balance (outstanding amount owed) */
export const selectUsedBalance = createSelector(
  [selectBalance],
  (balance) => balance?.usedBalance ?? 0
)

/** Extra allowance above credit limit */
export const selectAllowance = createSelector(
  [selectBalance],
  (balance) => balance?.allowance ?? 0
)

/** Wallet balance (for ecommerce prepaid customers) */
export const selectWalletBalance = createSelector(
  [selectBalance],
  (balance) => balance?.walletBalance ?? 0
)

/** Account status: active, suspended, blocked */
export const selectAccountStatus = createSelector(
  [selectBalance],
  (balance) => balance?.accountStatus ?? 'active'
)

/** Payment terms: cod, prepaid, net_7, net_30, etc */
export const selectPaymentTerms = createSelector(
  [selectBalance],
  (balance) => balance?.paymentTerms ?? 'cod'
)

/** VIBAN number for bank transfers */
export const selectVibanNumber = createSelector(
  [selectBalance],
  (balance) => balance?.vibanNumber
)

/** Check if customer can place orders (based on account status) */
export const selectCanPlaceOrders = createSelector(
  [selectAccountStatus],
  (status) => status === 'active'
)

/** Check if last balance check was successful */
export const selectBalanceCheckAllowed = createSelector(
  [selectLastBalanceCheck],
  (check) => check?.allowed ?? true
)

/** Get shortfall amount from last balance check */
export const selectBalanceShortfall = createSelector(
  [selectLastBalanceCheck],
  (check) => check?.shortfall ?? 0
)

// Transaction selectors
export const selectFilteredTransactions = createSelector([selectTransactions, selectFinanceFilter], (transactions, filter) => {
  // Ensure transactions is always an array
  let result = Array.isArray(transactions) ? transactions : []
  if (filter.type && filter.type !== 'all') result = result.filter(t => t.type === filter.type)
  if (filter.status && filter.status !== 'all') result = result.filter(t => t.status === filter.status)
  if (filter.dateRange) {
    result = result.filter(t => t.date >= filter.dateRange!.from && t.date <= filter.dateRange!.to)
  }
  if (filter.search) { const s = filter.search.toLowerCase(); result = result.filter(t => t.transactionNumber.toLowerCase().includes(s) || t.description.toLowerCase().includes(s)) }
  return result
})

export const selectPendingTransactions = createSelector([selectTransactions], transactions => {
  const txns = Array.isArray(transactions) ? transactions : []
  return txns.filter(t => t.status === 'pending')
})

// Pagination selectors
export const selectPagination = (state: RootState) => state.finance.pagination
export const selectStatementLoading = (state: RootState) => state.finance.statementLoading
export const selectStatsLoading = (state: RootState) => state.finance.statsLoading

// Payment-specific selectors
export const selectPaymentsLoading = (state: RootState) => state.finance.paymentsLoading
export const selectPaymentsPagination = (state: RootState) => state.finance.paymentsPagination
export const selectCreatePaymentLoading = (state: RootState) => state.finance.createPaymentLoading
export const selectCreatePaymentError = (state: RootState) => state.finance.createPaymentError

// Invoice selectors
export const selectInvoices = (state: RootState) => state.finance.invoices
export const selectInvoicesLoading = (state: RootState) => state.finance.invoicesLoading
export const selectInvoicesPagination = (state: RootState) => state.finance.invoicesPagination
export const selectSelectedInvoice = (state: RootState) => state.finance.selectedInvoice
export const selectSelectedInvoiceLoading = (state: RootState) => state.finance.selectedInvoiceLoading
export const selectInvoiceFilter = (state: RootState) => state.finance.invoiceFilter
export const selectZatcaInvoices = (state: RootState) => state.finance.zatcaInvoices
export const selectZatcaInvoicesLoading = (state: RootState) => state.finance.zatcaInvoicesLoading
export const selectZatcaInvoicesPagination = (state: RootState) => state.finance.zatcaInvoicesPagination
export const selectSelectedZatcaInvoice = (state: RootState) => state.finance.selectedZatcaInvoice
export const selectSelectedZatcaInvoiceLoading = (state: RootState) => state.finance.selectedZatcaInvoiceLoading

// Retry payment sync selectors
export const selectRetryPaymentSyncLoading = (state: RootState) => state.finance.retryPaymentSyncLoading

// SAP balance selectors
export const selectSapBalances = (state: RootState) => state.finance.sapBalances
export const selectSapBalancesLoading = (state: RootState) => state.finance.sapBalancesLoading
export const selectSapBalancesError = (state: RootState) => state.finance.sapBalancesError
export const selectSapBalancesRefreshing = (state: RootState) => state.finance.sapBalancesRefreshing

/** Finance balance amount from SAP */
export const selectFinanceBalanceAmount = createSelector(
  [selectSapBalances],
  (sapBalances) => sapBalances?.financeBalance?.amount ?? null
)

/** Sum of all contract totalNetAmounts */
export const selectContractBalanceTotal = createSelector(
  [selectSapBalances],
  (sapBalances) => {
    if (!sapBalances?.contractBalances?.length) return null
    return sapBalances.contractBalances.reduce((sum, c) => sum + c.totalNetAmount, 0)
  }
)
